/*
 * Single source of truth for the city SEO landing pages (consumer pages for
 * Lowkey's core markets). Each entry drives: the App.js route + <Helmet> tags,
 * the inject-seo.js static head injection (title, description, and a per-city
 * FAQPage from `faq`), the Hero copy, and the CityIntro section (intro + faq).
 * All other Home sections render their default consumer copy because the channel
 * key does not match any branch in them.
 *
 * CommonJS (module.exports) so scripts/inject-seo.js (Node) and the React app
 * (webpack) can both consume it, same pattern as professions.js.
 *
 * faq: one genuinely city-specific Q&A (local panel brands and building types)
 * so each page carries unique content and its own FAQPage schema. Double-quoted
 * because answers contain apostrophes.
 *
 * hideFromCityIndex: a non-city landing page (e.g. /enterphone, a regional panel
 * brand) that reuses this pipeline but is left off the /cities list.
 */
const CITY_PAGES = {
  toronto: {
    path: '/toronto',
    name: 'Toronto',
    seoTitle: 'The Apartment Buzzer App for Toronto Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Toronto condo or apartment buzzer from your phone, anywhere in the GTA. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Toronto',
    heroSubtext:
      'Control your Toronto apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Toronto buildings',
      paragraphs: [
        'Most Toronto buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone number, including the Enterphone and Mircom systems found across GTA towers.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work with the Enterphone or Mircom panel in my Toronto building?",
        a: "Yes, as long as the panel dials a phone number when a visitor buzzes your unit, which the Enterphone and Mircom systems in most GTA condos and apartments do. If your phone rings today when someone buzzes you, Lowkey can forward and automate that call.",
      },
    ],
  },
  vancouver: {
    path: '/vancouver',
    name: 'Vancouver',
    seoTitle: 'The Apartment Buzzer App for Vancouver Apartments & Stratas | Lowkey Buzzer',
    metaDescription:
      'Answer your Vancouver condo or apartment buzzer from your phone, anywhere in Metro Vancouver. Works with most telephone entry panels. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Vancouver',
    heroSubtext:
      'Control your Vancouver apartment or strata buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Vancouver buildings',
      paragraphs: [
        'Most Vancouver buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone number, including the Enterphone systems found in many Vancouver and Burnaby buildings.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work with the Enterphone system common in Vancouver buildings?",
        a: "Yes. Enterphone and similar panels place an outbound call to a resident when someone buzzes, and Lowkey works with any buzzer that dials a phone number. If your phone rings when a visitor buzzes your unit today, you are compatible.",
      },
    ],
  },
  enterphone: {
    path: '/enterphone',
    name: 'Enterphone',
    hideFromCityIndex: true,
    seoTitle: 'Enterphone App: Answer Your Enterphone From Any Phone | Lowkey Buzzer',
    metaDescription:
      'Answer your condo or apartment Enterphone from your cell, ring every phone in your home, and auto buzz in guests with passcodes. No hardware to install.',
    heroTitle: 'The Enterphone App for Your Cell Phone',
    heroSubtext:
      'Answer your condo Enterphone from anywhere and let guests in automatically.',
    intro: {
      heading: 'Works with your Enterphone',
      paragraphs: [
        'When a visitor finds your name on an Enterphone, the panel phones the number on file and a keypress opens the front door. Lowkey gives your unit a virtual number for that directory entry, so the call rings the app on every phone you add and can buzz expected guests in for you.',
      ],
    },
    faq: [
      {
        q: 'Does Lowkey work with my Enterphone?',
        a: "Yes, if your Enterphone dials a phone number when someone buzzes your unit, which is how most condo and apartment Enterphone panels in Canada work. If your cell rings today when a visitor buzzes, you are compatible. Older panels that only ring a handset wired inside your suite need the building to switch your entry to an outside number first.",
      },
      {
        q: 'Do I need my strata or property manager to set it up?',
        a: "Only once. Residents cannot edit the Enterphone directory themselves, so you send your building manager the Lowkey number for your unit. It is the same routine change the building makes when a resident gets a new phone number, and nothing is installed on the panel.",
      },
      {
        q: 'Can guests use a code on the Enterphone keypad?',
        a: "Yes. After a guest selects your entry, Lowkey answers and they enter a 4-digit passcode you created on the Enterphone keypad to open the front door. Passcodes can be scheduled, recurring, or set to expire, so a cleaner or dog walker only gets in when you expect them.",
      },
    ],
  },
  'los-angeles': {
    path: '/los-angeles',
    name: 'Los Angeles',
    seoTitle: 'The Apartment Buzzer App for Los Angeles Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your LA apartment or condo buzzer from your phone, anywhere from Downtown to the Valley. Works with most telephone entry and call box systems. No installation.',
    heroTitle: 'The Apartment Buzzer App for Los Angeles',
    heroSubtext:
      'Control your Los Angeles apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Los Angeles buildings',
      paragraphs: [
        'Most Los Angeles buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work with a DoorKing or Aiphone panel in LA?",
        a: "Yes, as long as it dials a phone number when a visitor buzzes, which DoorKing and Aiphone telephone entry systems do. Lowkey forwards and automates that call, whether you are in a Koreatown mid-rise or a Valley fourplex.",
      },
    ],
  },
  'san-diego': {
    path: '/san-diego',
    name: 'San Diego',
    seoTitle: 'The Apartment Buzzer App for San Diego Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your San Diego apartment or condo buzzer from your phone, anywhere from Downtown to North County. Works with most entry panels and call boxes. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for San Diego',
    heroSubtext:
      'Control your San Diego apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for San Diego buildings',
      paragraphs: [
        'Most San Diego buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work with the call box at my gated San Diego complex?",
        a: "If the call box dials your phone when a visitor enters your code, yes. Most San Diego gated complexes use DoorKing or Aiphone systems that do, and Lowkey forwards that call so you can let guests and deliveries in from anywhere.",
      },
    ],
  },
  seattle: {
    path: '/seattle',
    name: 'Seattle',
    seoTitle: 'The Apartment Buzzer App for Seattle Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Seattle apartment or condo buzzer from your phone, anywhere from Capitol Hill to Bellevue. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Seattle',
    heroSubtext:
      'Control your Seattle apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Seattle buildings',
      paragraphs: [
        'Most Seattle buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing, Aiphone, and Linear.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work with a Capitol Hill apartment buzzer?",
        a: "Yes, if the panel dials your phone when a visitor buzzes your unit, which most Seattle telephone entry systems do. Lowkey is built here in Seattle and works with any buzzer that dials out to a phone.",
      },
    ],
  },
  portland: {
    path: '/portland',
    name: 'Portland',
    seoTitle: 'The Apartment Buzzer App for Portland Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Portland apartment or condo buzzer from your phone, anywhere from the Pearl to the east side. Works with most telephone entry panels. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Portland',
    heroSubtext:
      'Control your Portland apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Portland buildings',
      paragraphs: [
        'Most Portland buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work in a Pearl District loft?",
        a: "If the entry panel dials your phone when someone buzzes, yes. Most Portland buildings use telephone entry that dials out, and Lowkey forwards and automates that call. A hardwired handset with no outbound call is the exception.",
      },
    ],
  },
  nyc: {
    path: '/nyc',
    name: 'New York City',
    seoTitle: 'The Apartment Buzzer App for New York City Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your NYC apartment buzzer from your phone, anywhere across the five boroughs. Works with any intercom panel that dials out to a phone. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for New York City',
    heroSubtext:
      'Control your New York City apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for New York City buildings',
      paragraphs: [
        'Most New York City buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, so if your buzzer already rings your cell, you are set.',
      ],
    },
    faq: [
      {
        q: "My NYC building has an old intercom handset. Will Lowkey work?",
        a: "Only if the system places a phone call to you when a visitor buzzes. Many older NYC intercoms are wired handsets with no outbound call, and those cannot work with Lowkey. If your phone already rings when someone buzzes your unit, you are compatible.",
      },
    ],
  },
  calgary: {
    path: '/calgary',
    name: 'Calgary',
    seoTitle: 'The Apartment Buzzer App for Calgary Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Calgary condo or apartment buzzer from your phone, anywhere in the city. Works with most Enterphone and Mircom entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Calgary',
    heroSubtext:
      'Control your Calgary apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Calgary buildings',
      paragraphs: [
        'Most Calgary buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems common across the city.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work with the buzzer in my Beltline condo?",
        a: "If the lobby panel dials your phone when someone buzzes, yes. Most Calgary condos and apartments use Enterphone or Mircom telephone entry that does exactly that, and Lowkey forwards and automates that call. Buildings with an old wired handset and no outbound call are the exception.",
      },
    ],
  },
  surrey: {
    path: '/surrey',
    name: 'Surrey',
    seoTitle: 'The Apartment Buzzer App for Surrey Apartments & Stratas | Lowkey Buzzer',
    metaDescription:
      'Answer your Surrey condo or apartment buzzer from your phone, anywhere in the city. Works with most Enterphone and Mircom entry panels. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Surrey',
    heroSubtext:
      'Control your Surrey apartment or strata buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Surrey buildings',
      paragraphs: [
        'Most Surrey buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems found across the Lower Mainland.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work in a newer Surrey condo tower?",
        a: "Almost certainly. Newer Surrey towers use telephone entry panels like Enterphone and Mircom that dial a phone when a visitor buzzes, which is exactly what Lowkey forwards and automates. The quick test is whether your phone rings today when someone buzzes your unit.",
      },
    ],
  },
  'san-francisco': {
    path: '/san-francisco',
    name: 'San Francisco',
    seoTitle: 'The Apartment Buzzer App for San Francisco Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your San Francisco apartment or condo buzzer from your phone, anywhere in the city. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for San Francisco',
    heroSubtext:
      'Control your San Francisco apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for San Francisco buildings',
      paragraphs: [
        'Most San Francisco buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work in a Victorian walk-up in SF?",
        a: "If the front-door panel dials your phone when a visitor buzzes, yes. Most San Francisco buildings use telephone entry that does, though a hardwired handset with no outbound call does not. If your phone rings today when someone buzzes, you are compatible.",
      },
    ],
  },
  'san-jose': {
    path: '/san-jose',
    name: 'San Jose',
    seoTitle: 'The Apartment Buzzer App for San Jose Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your San Jose apartment or condo buzzer from your phone, anywhere across Silicon Valley. Works with most entry panels and call boxes. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for San Jose',
    heroSubtext:
      'Control your San Jose apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for San Jose buildings',
      paragraphs: [
        'Most San Jose buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work with my San Jose complex call box?",
        a: "If the call box or panel dials your phone when a visitor buzzes, yes. Most San Jose buildings and gated complexes use DoorKing or Aiphone systems that do, and Lowkey forwards that call so you can let people in from anywhere.",
      },
    ],
  },
  dallas: {
    path: '/dallas',
    name: 'Dallas',
    seoTitle: 'The Apartment Buzzer App for Dallas Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Dallas apartment buzzer or gate call box from your phone, anywhere in the metroplex. Works with most systems that dial a phone. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Dallas',
    heroSubtext:
      'Control your Dallas apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Dallas buildings',
      paragraphs: [
        'Most Dallas buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any call box or panel that dials out to a phone, including common DoorKing gate systems.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work with the gate call box at my Dallas complex?",
        a: "If the gate call box dials your phone when a visitor enters your code, yes. Most Dallas gated communities use DoorKing systems that do, and Lowkey forwards that call so you can open the gate from anywhere.",
      },
    ],
  },
  austin: {
    path: '/austin',
    name: 'Austin',
    seoTitle: 'The Apartment Buzzer App for Austin Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Austin apartment buzzer or gate call box from your phone, anywhere in the city. Works with most systems that dial a phone. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Austin',
    heroSubtext:
      'Control your Austin apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Austin buildings',
      paragraphs: [
        'Most Austin buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any call box or panel that dials out to a phone, including common DoorKing gate systems.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work with my Austin apartment gate?",
        a: "If the gate call box places a phone call when someone enters your code, yes. Most Austin gated complexes use DoorKing gate systems that do, and Lowkey forwards and automates that call.",
      },
    ],
  },
  'san-antonio': {
    path: '/san-antonio',
    name: 'San Antonio',
    seoTitle: 'The Apartment Buzzer App for San Antonio Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your San Antonio apartment buzzer or gate call box from your phone, anywhere in the city. Works with most systems that dial a phone. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for San Antonio',
    heroSubtext:
      'Control your San Antonio apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for San Antonio buildings',
      paragraphs: [
        'Most San Antonio buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any call box or panel that dials out to a phone, including common DoorKing gate systems.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work with the call box at my gated San Antonio community?",
        a: "Yes, if the call box dials your phone when a visitor enters your code, which most San Antonio DoorKing gate systems do. Lowkey forwards that call so you can let guests and deliveries through the gate from anywhere.",
      },
    ],
  },
  tampa: {
    path: '/tampa',
    name: 'Tampa',
    seoTitle: 'The Apartment Buzzer App for Tampa Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Tampa apartment buzzer or gate call box from your phone, anywhere in the bay area. Works with most systems that dial a phone. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Tampa',
    heroSubtext:
      'Control your Tampa apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Tampa buildings',
      paragraphs: [
        'Most Tampa buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any call box or panel that dials out to a phone, including common DoorKing gate systems.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work with a gated Tampa community call box?",
        a: "If the call box dials your phone when a visitor enters your code, yes. Most Tampa gated communities and condos use DoorKing systems that do, and Lowkey forwards that call so you can open the gate remotely.",
      },
    ],
  },
  miami: {
    path: '/miami',
    name: 'Miami',
    seoTitle: 'The Apartment Buzzer App for Miami Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Miami condo buzzer or gate call box from your phone, anywhere in the city. Works with most systems that dial a phone. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Miami',
    heroSubtext:
      'Control your Miami apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Miami buildings',
      paragraphs: [
        'Most Miami buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel or call box that dials out to a phone, including common systems such as DoorKing and Comelit.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work with the call box or panel at my Brickell condo?",
        a: "If it dials your phone when a visitor buzzes, yes. Most Miami condos and gated communities use DoorKing or Comelit systems that do, and Lowkey forwards and automates that call.",
      },
    ],
  },
  orlando: {
    path: '/orlando',
    name: 'Orlando',
    seoTitle: 'The Apartment Buzzer App for Orlando Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Orlando apartment buzzer or gate call box from your phone, anywhere in the area. Works with most systems that dial a phone. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Orlando',
    heroSubtext:
      'Control your Orlando apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Orlando buildings',
      paragraphs: [
        'Most Orlando buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any call box or panel that dials out to a phone, including common DoorKing gate systems.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work with my gated Orlando community gate?",
        a: "If the gate call box dials your phone when a visitor enters your code, yes. Most Orlando gated communities use DoorKing systems that do, and Lowkey forwards that call so you can open the gate from anywhere.",
      },
    ],
  },
  chicago: {
    path: '/chicago',
    name: 'Chicago',
    seoTitle: 'The Apartment Buzzer App for Chicago Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Chicago apartment or condo buzzer from your phone, anywhere in the city. Works with most telephone entry panels. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Chicago',
    heroSubtext:
      'Control your Chicago apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Chicago buildings',
      paragraphs: [
        'Most Chicago buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work in a vintage Chicago three-flat?",
        a: "It works if the building's buzzer dials a phone when a visitor buzzes your unit. Many Chicago buildings, vintage and new, use telephone entry that does; a hardwired handset with no outbound call does not. If your phone rings today when someone buzzes, you are set.",
      },
    ],
  },
  montreal: {
    path: '/montreal',
    name: 'Montreal',
    seoTitle: 'The Apartment Buzzer App for Montreal Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Montreal apartment or condo buzzer from your phone, anywhere on the island. Works with most telephone entry and interphone panels. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Montreal',
    heroSubtext:
      'Control your Montreal apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Montreal buildings',
      paragraphs: [
        'Most Montreal buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems common across Quebec.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work with a Montreal interphone system?",
        a: "Yes, as long as the interphone dials a phone number when a visitor buzzes, which most Montreal telephone entry panels do. If your phone rings today when someone buzzes your unit, Lowkey can forward and automate that call. It is not for old wired handsets that never place a call.",
      },
    ],
  },
  atlanta: {
    path: '/atlanta',
    name: 'Atlanta',
    seoTitle: 'The Apartment Buzzer App for Atlanta Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Atlanta apartment buzzer or gate call box from your phone, anywhere in metro Atlanta. Works with most systems that dial a phone. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Atlanta',
    heroSubtext:
      'Control your Atlanta apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Atlanta buildings',
      paragraphs: [
        'Most Atlanta buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any call box or panel that dials out to a phone, including common DoorKing gate systems.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work with the call box at my gated Atlanta community?",
        a: "Yes, if the call box dials your phone when a visitor enters your code, which most Atlanta DoorKing gate systems do. Lowkey forwards that call so you can let guests and deliveries in from anywhere.",
      },
    ],
  },
  london: {
    path: '/london',
    name: 'London, Ontario',
    seoTitle: 'The Apartment Buzzer App for London, Ontario Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your London, Ontario apartment or condo buzzer from your phone, anywhere in the city. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for London, Ontario',
    heroSubtext:
      'Control your London, Ontario apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for London, Ontario buildings',
      paragraphs: [
        'Most London, Ontario buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems common across Ontario.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work in a student rental near Western?",
        a: "Yes, if the building's panel dials a phone when someone buzzes your unit. Most London apartment buildings use telephone entry that does, and Lowkey forwards that call so any roommate can buzz guests in. Wired handsets with no outbound call are the exception.",
      },
    ],
  },
  kitchener: {
    path: '/kitchener',
    name: 'Kitchener',
    seoTitle: 'The Apartment Buzzer App for Kitchener Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Kitchener or Waterloo apartment or condo buzzer from your phone, anywhere in the region. Works with most telephone entry panels. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Kitchener',
    heroSubtext:
      'Control your Kitchener apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Kitchener and Waterloo buildings',
      paragraphs: [
        'Most Kitchener buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems common across Ontario.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work in a Kitchener or Waterloo condo?",
        a: "If the lobby panel dials your phone when a visitor buzzes, yes. Most buildings in the region use Enterphone or Mircom telephone entry that does, and Lowkey forwards and automates that call.",
      },
    ],
  },
  'washington-dc': {
    path: '/washington-dc',
    name: 'Washington DC',
    seoTitle: 'The Apartment Buzzer App for Washington DC Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your DC apartment or condo buzzer from your phone, anywhere in the DMV. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Washington DC',
    heroSubtext:
      'Control your Washington DC apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Washington DC buildings',
      paragraphs: [
        'Most Washington DC buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work in a DC rowhouse condo or apartment?",
        a: "If the entry panel dials your phone when a visitor buzzes, yes. Most DC buildings use telephone entry that dials out, and Lowkey forwards that call and can buzz guests in automatically.",
      },
    ],
  },
  'orange-county': {
    path: '/orange-county',
    name: 'Orange County',
    seoTitle: 'The Apartment Buzzer App for Orange County Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Orange County apartment buzzer or gate call box from your phone, anywhere from Irvine to the coast. Works with most systems that dial a phone. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Orange County',
    heroSubtext:
      'Control your Orange County apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Orange County buildings',
      paragraphs: [
        'Most Orange County buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any call box or panel that dials out to a phone, including common DoorKing gate systems.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work with the gate call box at my Irvine community?",
        a: "Yes, if the gate call box dials your phone when a visitor enters your code, which most Orange County DoorKing gate systems do. Lowkey forwards that call so you can open the gate from anywhere.",
      },
    ],
  },
  victoria: {
    path: '/victoria',
    name: 'Victoria',
    seoTitle: 'The Apartment Buzzer App for Victoria Apartments & Stratas | Lowkey Buzzer',
    metaDescription:
      'Answer your Victoria condo or apartment buzzer from your phone, anywhere on the island. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Victoria',
    heroSubtext:
      'Control your Victoria apartment or strata buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Victoria buildings',
      paragraphs: [
        'Most Victoria buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems common across BC.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work in a downtown Victoria condo?",
        a: "Yes, as long as the entry panel dials a phone when someone buzzes your unit, which most Greater Victoria buildings do. If your phone rings today when a visitor buzzes you, Lowkey is compatible.",
      },
    ],
  },
  ottawa: {
    path: '/ottawa',
    name: 'Ottawa',
    seoTitle: 'The Apartment Buzzer App for Ottawa Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Ottawa condo or apartment buzzer from your phone, anywhere in the city. Works with most telephone entry panels. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Ottawa',
    heroSubtext:
      'Control your Ottawa apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Ottawa buildings',
      paragraphs: [
        'Most Ottawa buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems common across Ontario.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work in my Centretown building?",
        a: "If the front-door panel calls your phone when a visitor buzzes, yes. Most Ottawa condos and apartments use telephone entry like Enterphone and Mircom that dial out, and Lowkey forwards that call and can buzz people in automatically.",
      },
    ],
  },
  boston: {
    path: '/boston',
    name: 'Boston',
    seoTitle: 'The Apartment Buzzer App for Boston Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Boston apartment or condo buzzer from your phone, anywhere in Greater Boston. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Boston',
    heroSubtext:
      'Control your Boston apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Boston buildings',
      paragraphs: [
        'Most Boston buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work in a Back Bay brownstone or a triple-decker?",
        a: "If the front-door panel dials your phone when a visitor buzzes, yes. Most Boston buildings use telephone entry that does, though some old wired handsets do not place a call. The quick test is whether your phone rings today when someone buzzes you.",
      },
    ],
  },
  'jersey-city': {
    path: '/jersey-city',
    name: 'Jersey City',
    seoTitle: 'The Apartment Buzzer App for Jersey City Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Jersey City or Hoboken apartment buzzer from your phone, anywhere in North Jersey. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Jersey City',
    heroSubtext:
      'Control your Jersey City apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Jersey City buildings',
      paragraphs: [
        'Most Jersey City buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work in a Jersey City high-rise?",
        a: "Yes, if the building's panel dials your phone when a visitor buzzes your unit, which most Jersey City and Hoboken high-rises do. Lowkey forwards and automates that call so you can buzz people in even while commuting on the PATH.",
      },
    ],
  },
  winnipeg: {
    path: '/winnipeg',
    name: 'Winnipeg',
    seoTitle: 'The Apartment Buzzer App for Winnipeg Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Winnipeg condo or apartment buzzer from your phone, anywhere in the city. Works with most telephone entry panels. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Winnipeg',
    heroSubtext:
      'Control your Winnipeg apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Winnipeg buildings',
      paragraphs: [
        'Most Winnipeg buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems common across the Prairies.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work with an Exchange District loft buzzer?",
        a: "If the panel dials your phone when a visitor buzzes, yes. Most Winnipeg buildings use telephone entry that dials out, and Lowkey forwards that call and can buzz people in automatically.",
      },
    ],
  },
  oakland: {
    path: '/oakland',
    name: 'Oakland',
    seoTitle: 'The Apartment Buzzer App for Oakland Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Oakland or East Bay apartment or condo buzzer from your phone, anywhere in the area. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Oakland',
    heroSubtext:
      'Control your Oakland apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Oakland and East Bay buildings',
      paragraphs: [
        'Most Oakland buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work in a converted Oakland loft?",
        a: "If the entry panel dials your phone when a visitor buzzes, yes. Most East Bay buildings use telephone entry that dials out, and Lowkey forwards that call so you can buzz people in even from across the bridge.",
      },
    ],
  },
  hartford: {
    path: '/hartford',
    name: 'Hartford',
    seoTitle: 'The Apartment Buzzer App for Hartford Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Hartford apartment or condo buzzer from your phone, anywhere in the area. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Hartford',
    heroSubtext:
      'Control your Hartford apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Hartford buildings',
      paragraphs: [
        'Most Hartford buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work in my Hartford apartment?",
        a: "Yes, if the building's buzzer dials a phone when a visitor buzzes your unit, which most Hartford telephone entry panels do. Lowkey forwards that call and can buzz people in automatically.",
      },
    ],
  },
  'new-haven': {
    path: '/new-haven',
    name: 'New Haven',
    seoTitle: 'The Apartment Buzzer App for New Haven Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your New Haven apartment or condo buzzer from your phone, anywhere in the area. Works with most telephone entry panels. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for New Haven',
    heroSubtext:
      'Control your New Haven apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for New Haven buildings',
      paragraphs: [
        'Most New Haven buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work in an apartment near Yale?",
        a: "If the panel dials your phone when a visitor buzzes, yes. Most New Haven buildings use telephone entry that does exactly that, and Lowkey forwards that call so any roommate can buzz guests in.",
      },
    ],
  },
  detroit: {
    path: '/detroit',
    name: 'Detroit',
    seoTitle: 'The Apartment Buzzer App for Detroit Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Detroit apartment or condo buzzer from your phone, anywhere in the metro. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Detroit',
    heroSubtext:
      'Control your Detroit apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Detroit buildings',
      paragraphs: [
        'Most Detroit buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including common systems such as DoorKing and Aiphone.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work in a Midtown Detroit loft?",
        a: "Yes, if the entry panel dials your phone when a visitor buzzes, which most Detroit telephone entry systems do. Lowkey forwards and automates that call, with nothing to install.",
      },
    ],
  },
  regina: {
    path: '/regina',
    name: 'Regina',
    seoTitle: 'The Apartment Buzzer App for Regina Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Regina condo or apartment buzzer from your phone, anywhere in the city. Works with most telephone entry panels. Nothing to install.',
    heroTitle: 'The Apartment Buzzer App for Regina',
    heroSubtext:
      'Control your Regina apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Regina buildings',
      paragraphs: [
        'Most Regina buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems common across the Prairies.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work in my Regina apartment?",
        a: "Yes, if the building's buzzer dials a phone when a visitor buzzes your unit, which most Regina telephone entry panels do. Lowkey forwards and automates that call, with no hardware to install.",
      },
    ],
  },
  saskatoon: {
    path: '/saskatoon',
    name: 'Saskatoon',
    seoTitle: 'The Apartment Buzzer App for Saskatoon Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Saskatoon condo or apartment buzzer from your phone, anywhere in the city. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Saskatoon',
    heroSubtext:
      'Control your Saskatoon apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Saskatoon buildings',
      paragraphs: [
        'Most Saskatoon buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems common across the Prairies.',
      ],
    },
    faq: [
      {
        q: "Does Lowkey work in a riverfront Saskatoon condo?",
        a: "If the lobby panel dials your phone when someone buzzes, yes. Most Saskatoon buildings use telephone entry that does exactly that, and Lowkey forwards that call so you can buzz guests in from anywhere.",
      },
    ],
  },
  edmonton: {
    path: '/edmonton',
    name: 'Edmonton',
    seoTitle: 'The Apartment Buzzer App for Edmonton Apartments & Condos | Lowkey Buzzer',
    metaDescription:
      'Answer your Edmonton condo or apartment buzzer from your phone, anywhere in the city. Works with most telephone entry panels. No installation required.',
    heroTitle: 'The Apartment Buzzer App for Edmonton',
    heroSubtext:
      'Control your Edmonton apartment or condo buzzer from the convenience of your smartphone.',
    intro: {
      heading: 'Built for Edmonton buildings',
      paragraphs: [
        'Most Edmonton buildings use a telephone entry panel that dials a phone number when a visitor buzzes your unit. Lowkey works with any panel that dials out to a phone, including the Enterphone and Mircom systems common across Alberta.',
      ],
    },
    faq: [
      {
        q: "Will Lowkey work with my Oliver-area buzzer?",
        a: "Yes, as long as the panel dials a phone when a visitor buzzes your unit, which most Edmonton telephone entry systems do. If your phone rings today when someone buzzes you, Lowkey is compatible.",
      },
    ],
  },
};

module.exports = { CITY_PAGES };
